import dagre from "@dagrejs/dagre";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import { Edge, Node } from "@xyflow/react";
import { NodeData } from "../components/flow-step-node";

const NODE_WIDTH = 220;
const LAYOUT_DIRECTION = "TB";
const GRID_SIZE = 24;

// Apply dagre layout to nodes and edges
export const getLayoutedElements = (
  nodes: Node<NodeData>[],
  edges: Edge[],
): { nodes: Node<NodeData>[]; edges: Edge[] } => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({
    rankdir: LAYOUT_DIRECTION,
    ranksep: 96,
    nodesep: 72,
    edgesep: 20,
  });

  for (const node of nodes) {
    const { step } = node.data;
    const layout = step.layout();
    const isCompact = layout === BlockLayout.COMPACT;
    const width = isCompact ? 160 : NODE_WIDTH;
    const height = isCompact ? 80 : 100;
    dagreGraph.setNode(node.id, { width, height });
  }

  for (const edge of edges) {
    dagreGraph.setEdge(edge.source, edge.target);
  }

  dagre.layout(dagreGraph);

  // Build edge target->source map for post-processing
  const edgeSourceByTarget = new Map(edges.map((e) => [e.target, e.source]));

  const positionedNodes = new Map<string, { x: number; y: number }>();

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    const rawX = nodeWithPosition.x - nodeWithPosition.width / 2;
    const rawY = nodeWithPosition.y - nodeWithPosition.height / 2;
    const position = {
      x: Math.round(rawX / GRID_SIZE) * GRID_SIZE,
      y: Math.round(rawY / GRID_SIZE) * GRID_SIZE,
    };
    positionedNodes.set(node.id, position);
    return { ...node, position };
  });

  // Snap terminal End nodes directly below their drop-zone predecessor
  return {
    nodes: layoutedNodes.map((node) => {
      if (!node.id.endsWith("_terminal_end")) return node;
      const sourceId = edgeSourceByTarget.get(node.id);
      if (!sourceId) return node;
      const sourcePos = positionedNodes.get(sourceId);
      if (!sourcePos) return node;
      const sourceDagre = dagreGraph.node(sourceId);
      const endDagre = dagreGraph.node(node.id);
      return {
        ...node,
        position: {
          x:
            Math.round(
              (sourcePos.x + sourceDagre.width / 2 - endDagre.width / 2) /
                GRID_SIZE,
            ) * GRID_SIZE,
          y: sourcePos.y + sourceDagre.height + 16,
        },
      };
    }),
    edges,
  };
};
